import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { tv, type VariantProps } from "tailwind-variants";
import { Input } from "../../../core/inputs/Input";
import type { Page, PageType, QuestionBaseProps } from "../survey.config";

const SelectStyles = tv({
  slots: {
    Outer: "flex w-full flex-col",
  },
});

type TextProps<
  T extends PageType =
    | "ratingScale"
    | "ratingSlider"
    | "ratingStars"
    | "choice"
    | "choiceImage",
> = Page<T> &
  Pick<React.HtmlHTMLAttributes<HTMLDivElement>, "className"> &
  VariantProps<typeof SelectStyles> &
  Omit<QuestionBaseProps<T>, "onChange"> & {
    onChange: (value?: string) => void; // different from other components
    rows?: number;
    maxRows?: number;
    textRequired?: boolean;
    placeholder?: string;
    inputType?: React.HTMLInputTypeAttribute;
  };

const Text = ({
  className,
  mode,
  textTitle,
  updateQuestion,
  onChange,
  maxChar,
  textRequired,
  labels,
  value,
  ...props
}: TextProps) => {
  const { t } = useTranslation();
  const { Outer } = SelectStyles();

  const note = useMemo(() => {
    if (props.inputType === "number") {
      return [
        `Min: ${props.minValue ?? 0}`,
        props.maxValue && `Max: ${props.maxValue}`,
      ].join(", ");
    }
    if ((props?.value?.length || 0) > (maxChar || Number.POSITIVE_INFINITY)) {
      return t("util:tooLong", { val: t("global:description") });
    }
    return null;
  }, []);

  const inputProps = useMemo(() => {
    if (mode === "edit") {
      return {
        ...props,
        className: "text-text-subdued",
      };
    }
    return {
      tabIndex: props.tabIndex ?? undefined,
      placeholder: `${textTitle || props.placeholder} ${
        textRequired ? "" : `(${t("global:optional").toLowerCase()})`
      }`,
      required: textRequired,
      "aria-label": props["aria-label"],
      maxLength: maxChar,
      note: note || undefined,
    };
  }, [mode, note, textTitle, textRequired, maxChar, props]);

  return (
    <div className={Outer({ className })}>
      <Input
        {...inputProps}
        onChange={(event) => onChange(event.target.value)}
        value={mode === "edit" ? textTitle : value}
        type={(props.rows || 5) > 1 ? "textarea" : "text"}
        rows={props.rows || 5}
        maxRows={props.maxRows || 10}
      />
    </div>
  );
};
Text.displayName = "Textarea";
export default Text;
